// Estimate workspace state — shared by the header, KPI strip and every tab.
//
//   <EstimateWorkspaceProvider estimateId="EST-1005"> ... </EstimateWorkspaceProvider>
//   const ws = useWorkspace();
//
// draft    — editable copy of the estimate (undo/redo via useUndoableState)
// calc     — calculateEstimate(debounced draft): recomputed 150ms after the last edit
// actions  — save / recalculate / finalize / duplicate / export (mock service)
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import { useAlert } from "../../../../context/AlertContext";
import * as service from "../mock/estimateService.mock";
import { calculateEstimate, getLineDefaults, indexConfig, toTotalsSnapshot } from "../engine/calcMock";
import useUndoableState from "./useUndoableState";
import useDebouncedValue from "./useDebouncedValue";

const WorkspaceContext = createContext(null);

const DRAFT_KEYS = ["project", "lines", "equipmentOverrides", "gcOverrides", "inclusions", "exclusions"];
const pickDraft = (est) => Object.fromEntries(DRAFT_KEYS.map((k) => [k, est[k]]));

const userName = (user) => [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "You";

export function EstimateWorkspaceProvider({ estimateId, children }) {
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [estimate, setEstimate] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // "save" | "recalc" | "finalize" | "duplicate" | null
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [lastCalculatedAt, setLastCalculatedAt] = useState(null);

  const [draft, setDraft, { undo, redo, reset: resetHistory, canUndo, canRedo }] = useUndoableState(null);
  const savedDraftRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const est = await service.getEstimate(estimateId);
      const cfg = await service.getConfig(est.configVersionId);
      const d = pickDraft(est);
      savedDraftRef.current = d;
      resetHistory(d);
      setEstimate(est);
      setConfig(cfg);
      setLastCalculatedAt(est.lastCalculatedAt);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  useEffect(() => {
    load();
  }, [load]);

  const idx = useMemo(() => (config ? indexConfig(config) : null), [config]);

  // Recalculate on every edit, debounced 150ms; useMemo keeps it instant on re-render.
  const debouncedDraft = useDebouncedValue(draft, 150);
  const calc = useMemo(() => {
    if (!debouncedDraft || !config) return null;
    return calculateEstimate(debouncedDraft, config, idx);
  }, [debouncedDraft, config, idx]);

  const firstCalc = useRef(true);
  useEffect(() => {
    if (!calc) return;
    if (firstCalc.current) {
      firstCalc.current = false;
      return;
    }
    setLastCalculatedAt(new Date().toISOString());
  }, [calc]);

  const isLocked = estimate?.status === "FINALIZED";
  const dirty = !!draft && draft !== savedDraftRef.current;
  const calculating = draft !== debouncedDraft;

  // ------------------------------------------------------------ edits ----
  const guard = useCallback(
    (fn) =>
      (...args) => {
        if (isLocked) return;
        fn(...args);
      },
    [isLocked]
  );

  const updateLine = useMemo(
    () =>
      guard((lineId, patch) =>
        setDraft(
          (d) => ({ ...d, lines: d.lines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)) }),
          { coalesceKey: `${lineId}:${Object.keys(patch).join(",")}` }
        )
      ),
    [guard, setDraft]
  );

  /** Changing the surface re-applies that surface's defaults (like the Excel lookup did). */
  const changeSurface = useMemo(
    () =>
      guard((lineId, surfaceId) =>
        setDraft((d) => ({
          ...d,
          lines: d.lines.map((l) => {
            if (l.id !== lineId) return l;
            const next = { ...l, surfaceId, unit: l.unit || idx.surfaces[surfaceId]?.unit };
            return { ...next, ...getLineDefaults(next, config, d.project, idx) };
          }),
        }))
      ),
    [guard, setDraft, config, idx]
  );

  const resetLineField = useMemo(
    () =>
      guard((lineId, field) =>
        setDraft((d) => ({
          ...d,
          lines: d.lines.map((l) => (l.id === lineId ? { ...l, [field]: getLineDefaults(l, config, d.project, idx)[field] } : l)),
        }))
      ),
    [guard, setDraft, config, idx]
  );

  const addLine = useMemo(
    () =>
      guard((afterId) => {
        const id = `N${Date.now().toString(36)}`;
        setDraft((d) => {
          const after = d.lines.find((l) => l.id === afterId) || d.lines[d.lines.length - 1];
          const base = {
            id,
            section: after?.section || "wall",
            rawName: "Manual line",
            area: after?.area || "",
            surfaceId: after?.surfaceId || "walls-gyp",
            unit: after?.unit || "SF",
            nativeQty: 100,
            heightFt: null,
            specCode: null,
          };
          const line = { ...base, ...getLineDefaults(base, config, d.project, idx) };
          const i = d.lines.findIndex((l) => l.id === after?.id);
          const lines = [...d.lines];
          lines.splice(i + 1, 0, line);
          return { ...d, lines };
        });
        setSelectedLineId(id);
      }),
    [guard, setDraft, config, idx]
  );

  const duplicateLine = useMemo(
    () =>
      guard((lineId) =>
        setDraft((d) => {
          const i = d.lines.findIndex((l) => l.id === lineId);
          if (i < 0) return d;
          const lines = [...d.lines];
          lines.splice(i + 1, 0, { ...d.lines[i], id: `D${Date.now().toString(36)}`, rawName: `${d.lines[i].rawName} (copy)` });
          return { ...d, lines };
        })
      ),
    [guard, setDraft]
  );

  const deleteLines = useMemo(
    () =>
      guard((ids) => {
        const set = new Set(ids);
        setDraft((d) => ({ ...d, lines: d.lines.filter((l) => !set.has(l.id)) }));
        setSelectedLineId((cur) => (set.has(cur) ? null : cur));
      }),
    [guard, setDraft]
  );

  const setProject = useMemo(
    () => guard((patch) => setDraft((d) => ({ ...d, project: { ...d.project, ...patch } }), { coalesceKey: `project:${Object.keys(patch).join(",")}` })),
    [guard, setDraft]
  );

  const setOverride = useMemo(
    () =>
      guard((kind, itemId, qty) =>
        setDraft((d) => {
          const key = kind === "gc" ? "gcOverrides" : "equipmentOverrides";
          const next = { ...d[key] };
          if (qty === null || qty === "" || qty === undefined) delete next[itemId];
          else next[itemId] = Number(qty);
          return { ...d, [key]: next };
        }, { coalesceKey: `${kind}:${itemId}` })
      ),
    [guard, setDraft]
  );

  const setText = useMemo(
    () => guard((field, value) => setDraft((d) => ({ ...d, [field]: value }), { coalesceKey: `text:${field}` })),
    [guard, setDraft]
  );

  // ---------------------------------------------------------- actions ----
  const currentTotals = useCallback(() => toTotalsSnapshot(calculateEstimate(draft, config, idx)), [draft, config, idx]);

  const runAction = useCallback(
    async (kind, fn, successMsg) => {
      setBusy(kind);
      try {
        const result = await fn();
        if (successMsg) showAlert({ type: "success", message: successMsg });
        return result;
      } catch (e) {
        showAlert({ type: "error", message: e.message || "Request failed" });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [showAlert]
  );

  const saveDraft = useCallback(async () => {
    const est = await runAction("save", () => service.saveDraft(estimateId, draft, currentTotals(), userName(user)), "Draft saved (mock)");
    if (est) {
      savedDraftRef.current = draft;
      setEstimate(est);
    }
    return est;
  }, [runAction, estimateId, draft, currentTotals, user]);

  const recalculate = useCallback(async () => {
    const est = await runAction("recalc", () => service.logRecalculation(estimateId, currentTotals(), userName(user)), "Estimate recalculated");
    if (est) {
      setEstimate((cur) => ({ ...cur, history: est.history, totals: est.totals }));
      setLastCalculatedAt(est.lastCalculatedAt);
    }
  }, [runAction, estimateId, currentTotals, user]);

  const finalize = useCallback(async () => {
    const est = await runAction("finalize", () => service.finalizeEstimate(estimateId, draft, currentTotals(), userName(user)), "Estimate finalized and locked");
    if (est) {
      savedDraftRef.current = draft;
      setEstimate(est);
    }
  }, [runAction, estimateId, draft, currentTotals, user]);

  const duplicate = useCallback(async () => {
    const copy = await runAction("duplicate", () => service.duplicateEstimate(estimateId, userName(user)), "Duplicated as a new draft");
    if (copy) navigate(`/swiftboard/estimates/${copy.id}/takeoff`);
  }, [runAction, estimateId, user, navigate]);

  const exportAs = useCallback(
    (format) => runAction("export", () => service.exportEstimate(estimateId, format), `Export queued – ${format}`),
    [runAction, estimateId]
  );

  // Ctrl/Cmd+Z, Ctrl+Shift+Z / Ctrl+Y — skipped while typing in an input so native undo still works there.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // Browser-level guard for unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const value = {
    estimateId,
    estimate,
    config,
    idx,
    loading,
    error,
    reload: load,
    draft,
    calc,
    calculating,
    dirty,
    isLocked,
    busy,
    lastCalculatedAt,
    selectedLineId,
    setSelectedLineId,
    undo,
    redo,
    canUndo: canUndo && !isLocked,
    canRedo: canRedo && !isLocked,
    updateLine,
    changeSurface,
    resetLineField,
    addLine,
    duplicateLine,
    deleteLines,
    setProject,
    setOverride,
    setText,
    saveDraft,
    recalculate,
    finalize,
    duplicate,
    exportAs,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside <EstimateWorkspaceProvider>");
  return ctx;
};
